import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { fetchOrders, createOrder } from '@/services/orders'
import { fetchColors, fetchWireTypes } from '@/services/materials'
import { fetchSellingTypes } from '@/services/products'
import { fetchCustomers } from '@/services/customers'
import type { Order, OrderStatus } from '@/types/orders'
import type { MaterialColor, MaterialType } from '@/types/materials'
import type { SellingType } from '@/types/products'
import type { Customer } from '@/types/customers'
import {
  AlertCircle,
  Package,
  DollarSign,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
  CheckCircle2,
  CircleDashed,
  XCircle,
  Plus,
} from 'lucide-react'

type SortField = 'client' | 'full_price' | 'order_status' | 'created_at'
type SortDirection = 'asc' | 'desc' | null

const ORDER_STATUS_CONFIG: Record<OrderStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  new: { label: 'New', variant: 'default', icon: <CircleDashed className="h-3 w-3" /> },
  in_progress: { label: 'In Progress', variant: 'secondary', icon: <Clock className="h-3 w-3" /> },
  completed: { label: 'Completed', variant: 'outline', icon: <CheckCircle2 className="h-3 w-3" /> },
  cancelled: { label: 'Cancelled', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
}

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function Orders() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [orders, setOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Create order state
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [showDeadline, setShowDeadline] = useState(false)
  const [colors, setColors] = useState<MaterialColor[]>([])
  const [wireTypes, setWireTypes] = useState<MaterialType[]>([])
  const [sellingTypes, setSellingTypes] = useState<SellingType[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [newOrder, setNewOrder] = useState({
    dimensions: '', // "materialName square height width quantity" e.g. "2 40 1.5 10 40"
    profit: '',
    prePayment: '',
    changeType: '',
    deadline: '',
    colorId: '',
    wireTypeId: '',
    sellingTypeId: '',
    clientPhone: '',
  })
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false)

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchOrders()
        setOrders(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load orders')
      } finally {
        setIsLoading(false)
      }
    }

    loadOrders()
  }, [])

  // Load selectors when sheet opens
  useEffect(() => {
    if (isSheetOpen && colors.length === 0) {
      const loadSelectors = async () => {
        try {
          const [colorsData, wireTypesData, sellingTypesData, customersData] = await Promise.all([
            fetchColors(),
            fetchWireTypes(),
            fetchSellingTypes(),
            fetchCustomers(),
          ])
          setColors(colorsData)
          setWireTypes(wireTypesData)
          setSellingTypes(sellingTypesData)
          setCustomers(customersData)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load form data')
        }
      }
      loadSelectors()
    }
  }, [isSheetOpen, colors.length])

  const parseDimensions = (input: string): number[] | null => {
    const parts = input.trim().split(/\s+/)
    if (parts.length !== 5) return null
    const nums = parts.map((p) => parseFloat(p))
    if (nums.some(isNaN)) return null
    return nums
  }

  const filteredCustomers = useMemo(() => {
    if (!newOrder.clientPhone.trim()) return customers
    const search = newOrder.clientPhone.toLowerCase()
    return customers.filter(
      (c) =>
        c.phone_number?.toLowerCase().includes(search) ||
        c.full_name?.toLowerCase().includes(search)
    )
  }, [customers, newOrder.clientPhone])

  const handleCreateOrder = async () => {
    const { dimensions, colorId, wireTypeId, sellingTypeId, clientPhone, profit, prePayment } = newOrder
    const parsed = parseDimensions(dimensions)
    if (!parsed || !colorId || !wireTypeId || !sellingTypeId || !clientPhone.trim()) {
      return
    }

    setIsCreating(true)
    try {
      const created = await createOrder({
        texts: parsed as [number, number, number, number, number],
        profit: profit ? parseFloat(profit) : 0,
        pre_payment: prePayment ? parseFloat(prePayment) : 0,
        change_type: newOrder.changeType || null,
        deadline: showDeadline && newOrder.deadline ? new Date(newOrder.deadline).toISOString() : null,
        color: parseInt(colorId),
        wire_type: parseInt(wireTypeId),
        selling_type: parseInt(sellingTypeId),
        clients: clientPhone.trim(),
      })
      setOrders((prev) => [created, ...prev])
      setNewOrder({
        dimensions: '',
        profit: '',
        prePayment: '',
        changeType: '',
        deadline: '',
        colorId: '',
        wireTypeId: '',
        sellingTypeId: '',
        clientPhone: '',
      })
      setShowDeadline(false)
      setIsSheetOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create order')
    } finally {
      setIsCreating(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortField('created_at')
        setSortDirection('desc')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="ml-1 h-3 w-3" />
    }
    return <ArrowUpDown className="ml-1 h-3 w-3" />
  }

  const filteredOrders = useMemo(() => {
    let result = orders.filter((order) => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        order.client.full_name.toLowerCase().includes(searchLower) ||
        order.client.phone_number.toLowerCase().includes(searchLower) ||
        order.material.name.toLowerCase().includes(searchLower)

      const matchesStatus = statusFilter === 'all' || order.order_status === statusFilter

      return matchesSearch && matchesStatus
    })

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number
        let bValue: string | number

        switch (sortField) {
          case 'client':
            aValue = a.client.full_name
            bValue = b.client.full_name
            break
          case 'full_price':
            aValue = a.full_price
            bValue = b.full_price
            break
          case 'order_status':
            aValue = a.order_status
            bValue = b.order_status
            break
          case 'created_at':
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          default:
            return 0
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? comparison : -comparison
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1

        return 0
      })
    }

    return result
  }, [orders, searchQuery, statusFilter, sortField, sortDirection])

  const clearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
  }

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all'

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const totalOrders = orders.length
  const totalRevenue = orders.reduce((sum, o) => sum + o.full_price, 0)
  const totalPaid = orders.reduce((sum, o) => sum + o.paid, 0)
  const pendingOrders = orders.filter((o) => o.order_status === 'new' || o.order_status === 'in_progress').length

  return (
    <>
      <header className='flex h-16 shrink-0 items-center justify-between gap-2 px-4'>
        <div className='flex items-center gap-2'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('orders.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <Button size='sm' onClick={() => setIsSheetOpen(true)}>
          <Plus className='mr-1 h-4 w-4' />
          {t('orders.createOrder')}
        </Button>
      </header>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className='overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>{t('orders.createOrder')}</SheetTitle>
          </SheetHeader>
          <div className='space-y-4 p-4'>
            <div>
              <Label htmlFor='dimensions'>{t('orders.dimensions')}</Label>
              <Input
                id='dimensions'
                value={newOrder.dimensions}
                onChange={(e) => setNewOrder((prev) => ({ ...prev, dimensions: e.target.value }))}
                placeholder='2 40 1.5 10 40'
                className='mt-1 font-mono'
              />
              <p className='mt-1 text-xs text-muted-foreground'>
                {t('orders.dimensionsHint')}
              </p>
            </div>
            <div className='relative'>
              <Label htmlFor='clientPhone'>{t('orders.client')}</Label>
              <Input
                id='clientPhone'
                value={newOrder.clientPhone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d+]/g, '').slice(0, 13)
                  setNewOrder((prev) => ({ ...prev, clientPhone: value }))
                  setIsClientDropdownOpen(true)
                }}
                onFocus={() => setIsClientDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsClientDropdownOpen(false), 150)}
                placeholder='+998901234567'
                className='mt-1'
                autoComplete='off'
              />
              {isClientDropdownOpen && filteredCustomers.length > 0 && (
                <div className='absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md'>
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className='cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
                      onMouseDown={(e) => {
                        e.preventDefault()
                        setNewOrder((prev) => ({ ...prev, clientPhone: customer.phone_number || '' }))
                        setIsClientDropdownOpen(false)
                      }}
                    >
                      {customer.full_name} ({customer.phone_number})
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor='colorId'>{t('orders.color')}</Label>
              <Select
                value={newOrder.colorId}
                onValueChange={(value) => setNewOrder((prev) => ({ ...prev, colorId: value }))}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder={t('orders.selectColor')} />
                </SelectTrigger>
                <SelectContent>
                  {colors.map((color) => (
                    <SelectItem key={color.id} value={color.id.toString()}>
                      {color.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor='wireTypeId'>{t('orders.wireType')}</Label>
              <Select
                value={newOrder.wireTypeId}
                onValueChange={(value) => setNewOrder((prev) => ({ ...prev, wireTypeId: value }))}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder={t('orders.selectWireType')} />
                </SelectTrigger>
                <SelectContent>
                  {wireTypes.map((wt) => (
                    <SelectItem key={wt.id} value={wt.id.toString()}>
                      {wt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor='sellingTypeId'>{t('orders.sellingType')}</Label>
              <Select
                value={newOrder.sellingTypeId}
                onValueChange={(value) => setNewOrder((prev) => ({ ...prev, sellingTypeId: value }))}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder={t('orders.selectSellingType')} />
                </SelectTrigger>
                <SelectContent>
                  {sellingTypes.map((st) => (
                    <SelectItem key={st.id} value={st.id.toString()}>
                      {st.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label htmlFor='profit'>{t('orders.profit')} ({t('orders.optional')})</Label>
                <Input
                  id='profit'
                  type='number'
                  step='0.01'
                  value={newOrder.profit}
                  onChange={(e) => setNewOrder((prev) => ({ ...prev, profit: e.target.value }))}
                  placeholder='0.00'
                  className='mt-1'
                />
              </div>
              <div>
                <Label htmlFor='prePayment'>{t('orders.prePayment')} ({t('orders.optional')})</Label>
                <Input
                  id='prePayment'
                  type='number'
                  step='0.01'
                  value={newOrder.prePayment}
                  onChange={(e) => setNewOrder((prev) => ({ ...prev, prePayment: e.target.value }))}
                  placeholder='0.00'
                  className='mt-1'
                />
              </div>
            </div>
            <div>
              <Label htmlFor='changeType'>{t('orders.changeType')}</Label>
              <Select
                value={newOrder.changeType}
                onValueChange={(value) => setNewOrder((prev) => ({ ...prev, changeType: value }))}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder={t('orders.optional')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='discount'>{t('orders.discount')}</SelectItem>
                  <SelectItem value='markup'>{t('orders.markup')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='flex items-center justify-between'>
              <Label htmlFor='showDeadline'>{t('orders.setDeadline')}</Label>
              <Switch
                id='showDeadline'
                checked={showDeadline}
                onCheckedChange={setShowDeadline}
              />
            </div>
            {showDeadline && (
              <div>
                <Label htmlFor='deadline'>{t('orders.deadline')}</Label>
                <Input
                  id='deadline'
                  type='datetime-local'
                  value={newOrder.deadline}
                  onChange={(e) => setNewOrder((prev) => ({ ...prev, deadline: e.target.value }))}
                  className='mt-1'
                />
              </div>
            )}
          </div>
          <SheetFooter>
            <Button
              onClick={handleCreateOrder}
              disabled={
                isCreating ||
                !parseDimensions(newOrder.dimensions) ||
                !newOrder.colorId ||
                !newOrder.wireTypeId ||
                !newOrder.sellingTypeId ||
                !newOrder.clientPhone.trim()
              }
            >
              {isCreating ? t('common.loading') : t('common.create')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setError(null)}
            className='ml-auto'
          >
            {t('common.dismiss')}
          </Button>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-4'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('orders.totalOrders')}
              </CardTitle>
              <Package className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{totalOrders}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('orders.totalRevenue')}
              </CardTitle>
              <DollarSign className='h-4 w-4 text-green-500' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                {formatCurrency(totalRevenue)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('orders.totalPaid')}
              </CardTitle>
              <DollarSign className='h-4 w-4 text-blue-500' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-blue-600 dark:text-blue-400'>
                {formatCurrency(totalPaid)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('orders.pendingOrders')}
              </CardTitle>
              <Clock className='h-4 w-4 text-amber-500' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-amber-600 dark:text-amber-400'>
                {pendingOrders}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Orders Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t('orders.title')}</CardTitle>
            <CardDescription>
              {t('orders.manageOrders')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className='mb-4 flex flex-wrap items-center gap-3'>
              <div className='relative flex-1 min-w-[200px]'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder={t('orders.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as OrderStatus | 'all')}
              >
                <SelectTrigger className='w-[180px]'>
                  <SelectValue placeholder={t('orders.filterByStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('orders.allStatuses')}</SelectItem>
                  <SelectItem value='new'>{t('orders.new')}</SelectItem>
                  <SelectItem value='in_progress'>{t('orders.inProgress')}</SelectItem>
                  <SelectItem value='completed'>{t('orders.completed')}</SelectItem>
                  <SelectItem value='cancelled'>{t('orders.cancelled')}</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant='ghost' size='sm' onClick={clearFilters}>
                  <X className='mr-1 h-4 w-4' />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredOrders.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                {orders.length === 0
                  ? t('orders.noOrdersFound')
                  : t('orders.noOrdersMatch')}
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('client')}
                      >
                        <div className='flex items-center'>
                          {t('orders.client')}
                          {getSortIcon('client')}
                        </div>
                      </TableHead>
                      <TableHead>{t('orders.dimensions')}</TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('full_price')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('common.price')}
                          {getSortIcon('full_price')}
                        </div>
                      </TableHead>
                      <TableHead className='text-right'>{t('orders.paid')}</TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('order_status')}
                      >
                        <div className='flex items-center'>
                          {t('common.status')}
                          {getSortIcon('order_status')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('created_at')}
                      >
                        <div className='flex items-center'>
                          {t('common.created')}
                          {getSortIcon('created_at')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => {
                      const statusConfig = ORDER_STATUS_CONFIG[order.order_status]
                      return (
                        <TableRow
                          key={order.id}
                          className='cursor-pointer'
                          onClick={() => navigate(`/orders/${order.id}`)}
                        >
                          <TableCell>
                            <div className='font-medium'>
                              {order.client.full_name}
                            </div>
                            <div className='text-sm text-muted-foreground'>
                              {order.client.phone_number}
                            </div>
                          </TableCell>
                          <TableCell className='text-primary text-lg font-medium'>
                            {order.material.name} {order.square} {order.height} {order.width} {order.quantity}
                          </TableCell>
                          <TableCell className='text-right font-medium text-green-600 dark:text-green-400'>
                            {formatCurrency(order.full_price)}
                          </TableCell>
                          <TableCell className='text-right'>
                            <span
                              className={
                                order.paid < order.full_price
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-green-600 dark:text-green-400'
                              }
                            >
                              {formatCurrency(order.paid)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusConfig.variant} className='gap-1'>
                              {statusConfig.icon}
                              {t(`status.${order.order_status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {formatDate(order.created_at)}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Results count */}
            {!isLoading && orders.length > 0 && (
              <div className='mt-4 text-sm text-muted-foreground'>
                {t('common.showing')} {filteredOrders.length} {t('common.of')} {orders.length}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
